import { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Loader2, Search, Upload, FileCheck } from "lucide-react";
import { StepTitle, NextButton } from "./fields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SirenStep = ({ answers, set, next }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/calc/company`, { params: { q } });
      setResults(res.data.results);
    } catch (e) {
      setResults([]);
    }
    setLoading(false);
  };

  return (
    <>
      <StepTitle title="Quelle est votre entreprise ?" subtitle="Numéro SIREN ou nom de l'entreprise" />
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="SIREN ou raison sociale" className="h-12" data-testid="calc-siren-input" />
        <Button onClick={search} disabled={loading || q.trim().length < 3} className="h-12 px-6 bg-[#102692]" data-testid="calc-siren-search-btn">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
        </Button>
      </div>
      {results && (
        <div className="mt-4 space-y-2" data-testid="calc-siren-results">
          {results.length === 0 && <p className="text-sm text-gray-500">Aucune entreprise trouvée.</p>}
          {results.map((r) => (
            <button key={r.siren} onClick={() => next({ entreprise_en_creation: false, siren: r.siren, raison_sociale: r.raison_sociale,
              adresse: r.adresse, date_creation: r.date_creation, naf: r.naf })}
              className="w-full text-left p-3 rounded-xl border-2 border-gray-200 hover:border-[#102692]/40" data-testid={`calc-siren-result-${r.siren}`}>
              <p className="font-medium text-gray-900">{r.raison_sociale}</p>
              <p className="text-xs text-gray-500">SIREN {r.siren} · NAF {r.naf} · créée le {r.date_creation}</p>
              <p className="text-xs text-gray-500">{r.adresse}</p>
            </button>
          ))}
        </div>
      )}
      <label className="flex items-center gap-3 mt-5 text-sm text-gray-700 cursor-pointer">
        <Checkbox checked={!!answers.entreprise_en_creation}
          onCheckedChange={(c) => (c ? next({ entreprise_en_creation: true, siren: "", raison_sociale: "Entreprise en création" })
            : set({ entreprise_en_creation: false, raison_sociale: "" }))}
          data-testid="calc-siren-creation" />
        Entreprise en création
      </label>
    </>
  );
};

export const BonusStep = ({ answers, next, min = 0.5 }) => {
  const [coef, setCoef] = useState(answers.bonus_malus && answers.bonus_malus <= 1 ? answers.bonus_malus : 1);
  const [malus, setMalus] = useState(answers.bonus_malus > 1);
  return (
    <>
      <StepTitle title="Quel est votre coefficient bonus-malus ?" subtitle="Indiqué sur votre relevé d'information" />
      {!malus && (
        <div className="py-4">
          <p className="text-center text-5xl font-bold text-[#102692] mb-6" data-testid="calc-bonus-value">{coef.toFixed(2).replace(".", ",")}</p>
          <Slider min={min} max={1} step={0.01} value={[coef]} onValueChange={([v]) => setCoef(Math.round(v * 100) / 100)} data-testid="calc-bonus-slider" />
          <div className="flex justify-between text-xs text-gray-400 mt-2"><span>{min.toFixed(2).replace(".", ",")} (meilleur)</span><span>1,00</span></div>
        </div>
      )}
      <label className="flex items-center gap-3 mt-4 text-sm text-gray-700 cursor-pointer">
        <Checkbox checked={malus} onCheckedChange={(c) => setMalus(!!c)} data-testid="calc-bonus-malus-checkbox" />
        Mon coefficient est supérieur à 1,00 (malus)
      </label>
      <NextButton onClick={() => next({ bonus_malus: malus ? 1.25 : coef, malus_superieur_1: malus })} />
    </>
  );
};

export const UploadStep = ({ answers, next }) => {
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(answers.releve_filename ? { filename: answers.releve_filename, file_id: answers.releve_file_id } : null);

  const upload = async (f) => {
    if (!f) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", f);
    try {
      const res = await axios.post(`${API}/calc/upload`, form);
      setFile(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Envoi impossible");
    }
    setUploading(false);
  };

  return (
    <>
      <StepTitle title="Envoyez votre relevé d'information" subtitle="PDF ou photo — facultatif, accélère la validation de votre tarif" />
      <label className="flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed border-gray-300 rounded-2xl cursor-pointer hover:border-[#102692]/50" data-testid="calc-upload-zone">
        {uploading ? <Loader2 className="w-8 h-8 animate-spin text-[#102692]" /> : file ? <FileCheck className="w-8 h-8 text-emerald-500" /> : <Upload className="w-8 h-8 text-[#102692]" />}
        <span className="text-sm text-gray-600">{file ? file.filename : "Choisir un fichier (PDF, JPG, PNG — 10 Mo max)"}</span>
        <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="calc-upload-input" />
      </label>
      <NextButton disabled={uploading} label={file ? "Continuer" : "Passer cette étape"}
        onClick={() => next(file ? { releve_file_id: file.file_id, releve_filename: file.filename } : {})} />
    </>
  );
};
